import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BarChart3, Flame, Target, TrendingUp, Lock, Crown } from 'lucide-react';
import { format, subDays, startOfMonth, eachDayOfInterval } from 'date-fns';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { Goal, Habit } from '@/types/goal';
import { useProfile } from '@/hooks/useProfile';
import { getTierFromProfile, getLimitsFromProfile } from '@/lib/tier-utils';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { BETA_CONFIG } from '@/config/beta';

interface HabitHistoryHeatmapProps {
  goals: Goal[];
}

interface DayData {
  date: Date;
  dateStr: string;
  completions: number;
  isMilestone: boolean;
  milestoneType?: 'week1' | 'week2' | 'month1' | 'month3';
}

interface StreakInfo {
  longest: number;
  longestDate: Date | null;
  current: number;
}

// Generate heatmap data for a configurable number of days
const generateHeatmapData = (
  habits: Array<Habit & { completedDates: string[] }>,
  historyDays: number
): {
  weeks: DayData[][];
  streakInfo: StreakInfo;
  thisMonth: { completed: number; total: number };
  milestonesContributed: number;
  goalsCount: number;
} => {
  const today = new Date();
  const days: DayData[] = [];
  
  // Generate days based on history limit
  for (let i = historyDays; i >= 0; i--) {
    const date = subDays(today, i);
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Count completions for this day
    let completions = 0;
    habits.forEach(habit => {
      if (habit.completedDates.includes(dateStr)) {
        completions++;
      }
    });

    days.push({
      date,
      dateStr,
      completions,
      isMilestone: false,
    });
  }

  // Calculate streak info (within history limit)
  let longest = 0;
  let longestDate: Date | null = null;
  let current = 0;
  let tempStreak = 0;
  let tempStreakStart: Date | null = null;

  // Traverse from oldest to newest
  for (let i = 0; i < days.length; i++) {
    if (days[i].completions > 0) {
      if (tempStreak === 0) {
        tempStreakStart = days[i].date;
      }
      tempStreak++;
      if (tempStreak > longest) {
        longest = tempStreak;
        longestDate = tempStreakStart;
      }
    } else {
      tempStreak = 0;
      tempStreakStart = null;
    }
  }

  // Calculate current streak (from today backwards)
  for (let i = days.length - 1; i >= 0; i--) {
    if (days[i].completions > 0) {
      current++;
    } else {
      break;
    }
  }

  // Mark milestone days (7, 14, 30, 90 day marks in current streak)
  const milestoneMarks = [7, 14, 30, 90];
  if (current > 0) {
    milestoneMarks.forEach((mark, idx) => {
      if (current >= mark) {
        const dayIndex = days.length - mark;
        if (dayIndex >= 0) {
          days[dayIndex].isMilestone = true;
          days[dayIndex].milestoneType = ['week1', 'week2', 'month1', 'month3'][idx] as DayData['milestoneType'];
        }
      }
    });
  }

  // Calculate this month stats
  const monthStart = startOfMonth(today);
  const monthEnd = today;
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  let monthCompleted = 0;
  monthDays.forEach(day => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const hasCompletion = habits.some(h => h.completedDates.includes(dayStr));
    if (hasCompletion) monthCompleted++;
  });

  // Organize into weeks (columns)
  const weeks: DayData[][] = [];
  let currentWeek: DayData[] = [];
  
  // Pad the first week to align with day of week
  const firstDayOfWeek = days[0].date.getDay(); // 0 = Sunday
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push({
      date: subDays(days[0].date, firstDayOfWeek - i),
      dateStr: '',
      completions: -1, // indicates empty
      isMilestone: false,
    });
  }

  days.forEach(day => {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  // Push remaining days
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push({
        date: new Date(),
        dateStr: '',
        completions: -1,
        isMilestone: false,
      });
    }
    weeks.push(currentWeek);
  }

  // Count unique linked actions (milestones contributed)
  const linkedActionIds = new Set<string>();
  habits.forEach(habit => {
    if (habit.linkedAction?.actionId) {
      linkedActionIds.add(habit.linkedAction.actionId);
    }
  });

  return {
    weeks,
    streakInfo: { longest, longestDate, current },
    thisMonth: { completed: monthCompleted, total: monthDays.length },
    milestonesContributed: linkedActionIds.size,
    goalsCount: 0, // Will be passed separately
  };
};

// Calculate all-time longest streak for Pro users
const calculateAllTimeLongestStreak = (
  habits: Array<Habit & { completedDates: string[] }>
): { longest: number; longestDate: Date | null } => {
  // Gather all unique completion dates
  const allDatesSet = new Set<string>();
  habits.forEach(habit => {
    habit.completedDates.forEach(d => allDatesSet.add(d));
  });

  if (allDatesSet.size === 0) {
    return { longest: 0, longestDate: null };
  }

  // Sort dates
  const sortedDates = Array.from(allDatesSet).sort();
  
  let longest = 0;
  let longestDate: Date | null = null;
  let tempStreak = 0;
  let tempStreakStart: string | null = null;
  let prevDate: Date | null = null;

  for (const dateStr of sortedDates) {
    const currentDate = new Date(dateStr);
    
    if (prevDate) {
      const diffDays = Math.round((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        // Consecutive day
        tempStreak++;
      } else {
        // Gap - reset streak
        if (tempStreak > longest) {
          longest = tempStreak;
          longestDate = tempStreakStart ? new Date(tempStreakStart) : null;
        }
        tempStreak = 1;
        tempStreakStart = dateStr;
      }
    } else {
      // First date
      tempStreak = 1;
      tempStreakStart = dateStr;
    }
    
    prevDate = currentDate;
  }

  // Check final streak
  if (tempStreak > longest) {
    longest = tempStreak;
    longestDate = tempStreakStart ? new Date(tempStreakStart) : null;
  }

  return { longest, longestDate };
};

const getColorClass = (completions: number, isMilestone: boolean): string => {
  if (completions < 0) return 'fill-transparent';
  if (isMilestone) return 'fill-accent';
  if (completions === 0) return 'fill-muted/40';
  if (completions <= 2) return 'fill-primary/30';
  if (completions <= 4) return 'fill-primary/60';
  return 'fill-primary';
};

export const HabitHistoryHeatmap = ({ goals }: HabitHistoryHeatmapProps) => {
  const { profile } = useProfile();
  const { formatDate: fmtDate } = useDateFormat();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const userTier = getTierFromProfile(profile);
  const limits = getLimitsFromProfile(profile);
  const historyDays = limits.habit_history_days;
  const isPro = BETA_CONFIG.allFeaturesUnlocked || userTier === 'pro' || userTier === 'beta_pro';

  const allHabits = useMemo(() => {
    return goals.flatMap(goal => 
      goal.habits.map(habit => ({
        ...habit,
        goalId: goal.id,
      }))
    );
  }, [goals]);

  const { weeks, streakInfo, thisMonth, milestonesContributed } = useMemo(
    () => generateHeatmapData(allHabits, historyDays),
    [allHabits, historyDays]
  );

  // For Pro users, calculate all-time longest streak
  const allTimeStreak = useMemo(() => {
    if (isPro) {
      return calculateAllTimeLongestStreak(allHabits);
    }
    return null;
  }, [allHabits, isPro]);

  // Use all-time streak for Pro, limited streak for Free
  const displayedStreak = isPro && allTimeStreak 
    ? allTimeStreak 
    : { longest: streakInfo.longest, longestDate: streakInfo.longestDate };

  const goalsWithHabits = useMemo(() => {
    return goals.filter(g => g.habits.length > 0).length;
  }, [goals]);

  const monthPercentage = thisMonth.total > 0 
    ? Math.round((thisMonth.completed / thisMonth.total) * 100) 
    : 0;

  // Responsive square size
  const squareSize = { desktop: 14, mobile: 10 };
  const gap = 3;

  if (allHabits.length === 0) {
    return null;
  }

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          📊 Habit History
        </CardTitle>
        <CardDescription>
          Your completion patterns over the last {historyDays} days
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Heatmap */}
        <div className="overflow-hidden pb-2">
          <TooltipProvider delayDuration={100}>
            <svg
              className="block"
              width={weeks.length * (squareSize.desktop + gap) - gap}
              height={7 * (squareSize.desktop + gap) - gap}
              style={{ minWidth: weeks.length * (squareSize.mobile + gap) - gap }}
            >
              {weeks.map((week, weekIndex) => (
                <g key={weekIndex} transform={`translate(${weekIndex * (squareSize.desktop + gap)}, 0)`}>
                  {week.map((day, dayIndex) => (
                    <Tooltip key={`${weekIndex}-${dayIndex}`}>
                      <TooltipTrigger asChild>
                        <rect
                          x={0}
                          y={dayIndex * (squareSize.desktop + gap)}
                          width={squareSize.desktop}
                          height={squareSize.desktop}
                          rx={2}
                          className={`${getColorClass(day.completions, day.isMilestone)} transition-colors cursor-pointer hover:opacity-80 ${day.isMilestone ? 'stroke-accent stroke-1' : ''}`}
                        />
                      </TooltipTrigger>
                      {day.completions >= 0 && (
                        <TooltipContent 
                          side="top" 
                          className="bg-popover border-border text-sm"
                        >
                          <p className="font-medium">
                            {fmtDate(day.date)}
                          </p>
                          <p className="text-muted-foreground">
                            {day.completions === 0 
                              ? 'No habits completed'
                              : `${day.completions} habit${day.completions > 1 ? 's' : ''} completed`
                            }
                          </p>
                          {day.isMilestone && (
                            <p className="text-accent text-xs mt-1">
                              🎯 Milestone day!
                            </p>
                          )}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  ))}
                </g>
              ))}
            </svg>
          </TooltipProvider>

          {/* Legend */}
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <span>Less</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm bg-muted/40" />
              <div className="w-3 h-3 rounded-sm bg-primary/30" />
              <div className="w-3 h-3 rounded-sm bg-primary/60" />
              <div className="w-3 h-3 rounded-sm bg-primary" />
              <div className="w-3 h-3 rounded-sm bg-accent border border-accent" title="Milestone" />
            </div>
            <span>More</span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-3">
          {/* Longest Streak */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-accent/10">
                  <Flame className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{displayedStreak.longest} days</p>
                  <p className="text-sm text-muted-foreground">
                    Longest Streak {isPro ? '(all time)' : `(${historyDays} days)`}
                  </p>
                  {displayedStreak.longestDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Started {fmtDate(displayedStreak.longestDate)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* This Month */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-3xl font-bold">
                    {thisMonth.completed}/{thisMonth.total} days
                  </p>
                  <p className="text-sm text-muted-foreground">
                    This Month ({monthPercentage}%)
                  </p>
                  <Progress value={monthPercentage} className="h-1.5 mt-2" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Impact */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Target className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="text-base font-medium">
                    Contributed to {milestonesContributed} milestone{milestonesContributed !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Across {goalsWithHabits} goal{goalsWithHabits !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade prompt for Free users */}
        {!isPro && (
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium flex items-center gap-2">
                  See your full 90-day history with Pro
                  <Crown className="w-4 h-4 text-amber-500" />
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pro members get extended insights, all-time streak tracking, and advanced analytics.
                </p>
                <Button 
                  variant="link" 
                  className="h-auto p-0 mt-2 text-primary"
                  onClick={() => setUpgradeModalOpen(true)}
                >
                  Upgrade to Pro →
                </Button>
              </div>
            </div>
          </div>
        )}

        <UpgradeModal
          isOpen={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          trigger="history"
        />
      </CardContent>
    </Card>
  );
};
